import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Check, ArrowRight, Shield, GraduationCap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const applicationSchema = z.object({
  parentName: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(15),
  childGrade: z.string().min(1, "Child's grade is required"),
  currentChallenges: z.string().trim().min(1, "Please share your current challenges").max(1000),
  aiAwareness: z.string().min(1, "Please select AI awareness level"),
  educationBudget: z.string().min(1, "Please select education budget range"),
  learningGoals: z.string().max(1000).optional()
});

interface AdmissionFormProps {
  leadData: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

const AdmissionForm: React.FC<AdmissionFormProps> = ({ leadData }) => {
  const [formData, setFormData] = useState({
    parentName: leadData.name,
    email: leadData.email,
    phone: leadData.phone,
    childGrade: '',
    currentChallenges: '',
    aiAwareness: '',
    educationBudget: '',
    learningGoals: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = applicationSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          newErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('admission_applications')
        .insert({
          lead_id: leadData.id,
          parent_name: formData.parentName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          child_age: parseInt(formData.childGrade),
          current_challenges: formData.currentChallenges.trim(),
          ai_awareness: formData.aiAwareness,
          education_budget: formData.educationBudget,
          learning_goals: formData.learningGoals?.trim() || null
        });

      if (error) throw error;

      sessionStorage.setItem('userData', JSON.stringify({
        firstName: formData.parentName,
        email: formData.email
      }));

      toast({
        title: "Application Submitted!",
        description: "We'll be in touch with you soon about the next batch.",
      });

      navigate('/welcome');

    } catch (error) {
      console.error('Application error:', error);
      toast({
        title: "Submission Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-2xl">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <GraduationCap className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Apply for AI SuperCreators Program™</h3>
        <p className="text-gray-600">8-Week Live Online Program | Grades 6–12</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input
              type="text"
              name="parentName"
              value={formData.parentName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.parentName ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
              placeholder="Your full name"
            />
            {errors.parentName && <p className="text-red-500 text-xs mt-1">{errors.parentName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
              placeholder="you@example.com"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
              placeholder="Your phone number"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Child's Grade *</label>
            <select
              name="childGrade"
              value={formData.childGrade}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.childGrade ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
            >
              <option value="">Select grade</option>
              {Array.from({ length: 7 }, (_, i) => i + 6).map(grade => (
                <option key={grade} value={grade}>Grade {grade}</option>
              ))}
            </select>
            {errors.childGrade && <p className="text-red-500 text-xs mt-1">{errors.childGrade}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What are your biggest challenges in your child's education? *</label>
          <textarea
            name="currentChallenges"
            value={formData.currentChallenges}
            onChange={handleInputChange}
            rows={3}
            className={`w-full px-4 py-3 rounded-lg border ${errors.currentChallenges ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
            placeholder="e.g., keeping them engaged, preparing for future careers, screen time management..."
          />
          {errors.currentChallenges && <p className="text-red-500 text-xs mt-1">{errors.currentChallenges}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">How familiar are you with AI and its potential for education? *</label>
          <select
            name="aiAwareness"
            value={formData.aiAwareness}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.aiAwareness ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
          >
            <option value="">Select your level</option>
            <option value="Complete beginner">Complete beginner - never used AI</option>
            <option value="Heard about it">Heard about it but never tried</option>
            <option value="Basic user">Basic user - tried ChatGPT or similar</option>
            <option value="Regular user">Regular user - use AI tools frequently</option>
            <option value="Advanced user">Advanced user - understand AI capabilities well</option>
          </select>
          {errors.aiAwareness && <p className="text-red-500 text-xs mt-1">{errors.aiAwareness}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What's your annual education budget? *</label>
          <select
            name="educationBudget"
            value={formData.educationBudget}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.educationBudget ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-green-400`}
          >
            <option value="">Select range</option>
            <option value="Under ₹25,000">Under ₹25,000</option>
            <option value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</option>
            <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
            <option value="₹1,00,000 - ₹2,00,000">₹1,00,000 - ₹2,00,000</option>
            <option value="₹2,00,000 - ₹5,00,000">₹2,00,000 - ₹5,00,000</option>
            <option value="Above ₹5,00,000">Above ₹5,00,000</option>
          </select>
          {errors.educationBudget && <p className="text-red-500 text-xs mt-1">{errors.educationBudget}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">What do you hope your child achieves through AI learning?</label>
          <textarea
            name="learningGoals"
            value={formData.learningGoals}
            onChange={handleInputChange}
            rows={2}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="e.g., better problem-solving skills, career preparation, creative thinking..."
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 py-4 rounded-lg text-white text-lg font-bold transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:transform-none"
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              <span>Submitting...</span>
            </div>
          ) : (
            <>
              <Check className="w-5 h-5" />
              <span>Submit Application</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
          <Shield className="w-4 h-4" />
          <span>Your information is 100% secure and confidential</span>
        </div>
      </form>
    </div>
  );
};

export default AdmissionForm;
